import { useState } from "react";
import type { Suggestion } from "../types";

/** The browser's SpeechRecognition constructor, under either name. Typed here
 *  rather than globally — this is the only place Charlie touches it. */
type SpeechRecognitionCtor = new () => {
  lang: string;
  start(): void;
  onresult: ((e: { results: { [i: number]: { [j: number]: { transcript: string } } } }) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
};

interface SuggestionItemProps {
  sug: Suggestion;
  onAccept: (sug: Suggestion, notes: string, due: string) => void;
  onReject: (sug: Suggestion) => void;
  onRecords: () => void;
  /** This item's drill is in flight — the rows are fetched on the click. */
  busy?: boolean;
}

/**
 * One item in Charlie's brief: the prescribed action, the metric that raised
 * it, and the three things the user can do with it. Accepting expands an inline
 * form — notes (dictatable) plus a due date — because a task without the
 * administrator's own context is just the alert again.
 */
export function SuggestionItem({ sug, onAccept, onReject, onRecords, busy }: SuggestionItemProps) {
  const [mode, setMode] = useState<"idle" | "accepting">("idle");
  const [notes, setNotes] = useState("");
  const [due, setDue] = useState(sug.defaultDue);
  const [rec, setRec] = useState(false);

  function mic() {
    const w = window as unknown as {
      SpeechRecognition?: SpeechRecognitionCtor;
      webkitSpeechRecognition?: SpeechRecognitionCtor;
    };
    const SR = w.SpeechRecognition || w.webkitSpeechRecognition;
    if (!SR) {
      window.alert("Voice input needs Chrome, Edge, or Safari. In the iPad/phone apps this is the native keyboard mic.");
      return;
    }
    const r = new SR();
    r.lang = "en-US";
    setRec(true);
    r.onresult = (e) => setNotes((n) => (n ? n + " " : "") + e.results[0][0].transcript);
    r.onerror = () => setRec(false);
    r.onend = () => setRec(false);
    r.start();
  }

  // Decided items stay in place, greyed, so the list doesn't reshuffle under
  // the user mid-review.
  if (sug.status && sug.status !== "open") {
    return (
      <div className="item done">
        <div className="i-title">{sug.title}</div>
        <div className="stamp" style={sug.status === "rejected" ? { color: "var(--ink-3)" } : undefined}>
          {sug.status === "accepted" ? "✓ Added to My Tasks" : `✕ Rejected — ${sug.reason}`}
        </div>
      </div>
    );
  }

  return (
    <div className={"item" + (mode === "accepting" ? " accepting" : "")}>
      <div className="i-title">{sug.title}</div>
      <div className="i-meta">
        <span className="bchip">{sug.branch}</span>
        {sug.signal && <span>Signal: <b>{sug.signal}</b></span>}
      </div>
      <div className="acts">
        <button className="btn accept" onClick={() => setMode("accepting")}>Accept</button>
        <button className="btn reject" onClick={() => onReject(sug)}>Reject</button>
        {/* A signal with nothing behind it carries no drill type — no button.
            The type IS the label: "Open Orders", "Open Patients". */}
        {(sug.drill || sug.records.rows.length > 0) && (
          <button className="btn" onClick={onRecords} disabled={busy}>
            {busy ? "Opening…" : sug.drill ? `Open ${sug.drill.type}` : "Open records"}
          </button>
        )}
      </div>

      {mode === "accepting" && (
        <div className="form">
          <label>Notes</label>
          <div className="notes-wrap">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Asked Kim to take the Tue/Wed slots; I'll confirm with scheduling"
            />
            <button className={"mic" + (rec ? " rec" : "")} onClick={mic} aria-label="dictate">🎤</button>
          </div>
          <div className="frow">
            <label>Due date</label>
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          </div>
          <div className="acts">
            <button className="btn accept" onClick={() => onAccept(sug, notes, due)}>Create my task</button>
            <button className="btn" onClick={() => setMode("idle")}>Back</button>
          </div>
          <div className="hint">This task is yours. If you're handing it to someone, say so in the notes.</div>
        </div>
      )}
    </div>
  );
}
